import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Truck, CheckCircle2, Shield, Package, Car, ChevronLeft, ChevronRight, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedTransportEmail } from '../components/ObfuscatedTransportEmail';
import { GoogleBusinessSection } from '../components/GoogleBusiness';

const TRANSPORT_LOGO = "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/wu40yozp_Logo%20bw%20001%20-%20Kenscho%20Transport.png";

// Gallery images
const GALLERY_IMAGES = [
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/krkpto7y_IMG_6525.jpeg",
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/pcs6un1v_IMG_6839.jpeg",
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/y2ro62f3_IMG_6260.jpeg",
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/56loz1l5_IMG_6533.jpeg",
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/qs32i3nm_IMG_8122.jpeg",
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/848skps7_IMG_8097.jpeg",
  "https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/mf92tvg9_IMG_8023.jpeg",
];

// Dump trailer images
const DUMP_TRAILER_IMAGES = [
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/ab8pxhfu_IMG_3951.jpeg",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/c9tqp7bh_IMG_3972.jpeg",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/wkeaku00_IMG_5578.jpeg",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/sbgqka0l_IMG_2161.jpeg",
];

// Photo Gallery Component
const GallerySection = ({ language }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };

  const title = language === 'fr' ? 'Notre flotte en action' : 'Our Fleet in Action';

  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <h2 className="text-3xl font-light tracking-tight mb-10 text-center">
          {title}
        </h2>
        
        {/* Main Image */}
        <div className="relative mb-6">
          <div className="aspect-[16/9] rounded-lg overflow-hidden bg-muted">
            <img 
              src={GALLERY_IMAGES[currentIndex]} 
              alt={`Transport gallery ${currentIndex + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Navigation Arrows */}
          {GALLERY_IMAGES.length > 1 && (
            <>
              <button 
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button 
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>
        
        {/* Thumbnails */}
        {GALLERY_IMAGES.length > 1 && (
          <div className="flex justify-center gap-3">
            {GALLERY_IMAGES.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentIndex 
                    ? 'border-primary ring-2 ring-primary/30' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img 
                  src={img} 
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default function Transport() {
  const { language, getLocalizedLink } = useLanguage();
  const navigate = useNavigate();
  const [dumpTrailerIndex, setDumpTrailerIndex] = useState(0);

  // Google Ads Conversion Tracking - fires on button click
  const handleQuoteClick = (e) => {
    e.preventDefault();
    const url = getLocalizedLink('/contact');
    
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17938342185/F2cSCJKOu_cbEKnC1elC',
        'event_callback': () => {
          navigate(url);
        }
      });
      // Fallback in case callback doesn't fire within 1 second
      setTimeout(() => navigate(url), 1000);
    } else {
      navigate(url);
    }
  };

  const nextDumpImage = () => {
    setDumpTrailerIndex((prev) => (prev + 1) % DUMP_TRAILER_IMAGES.length);
  };

  const prevDumpImage = () => {
    setDumpTrailerIndex((prev) => (prev - 1 + DUMP_TRAILER_IMAGES.length) % DUMP_TRAILER_IMAGES.length);
  };

  const content = {
    en: {
      tagline: 'Kenscho Transport',
      title1: 'Hot Shot',
      title2: 'Transport Services',
      desc: 'Kenscho Transport provides reliable transport services for cargo, vehicles, and heavy equipment. Serving Montreal and surrounding areas with professional hauling solutions.',
      
      trailerTitle: 'Our Equipment',
      trailerSpecs: {
        title: '30 ft Flat Deck Trailer',
        capacity: '13,000 lbs (5,900 kg) capacity',
        features: [
          'Hydraulic dovetail for easy loading',
          'Electric winch for secure pulling',
          'Professional tie-down equipment',
          'Tarp services for weather protection'
        ]
      },
      
      dumpTrailerSpecs: {
        title: '5 Ton Dump Trailer',
        capacity: '8,000 lbs (3,600 kg) / 17 cubic yards',
        features: [
          'Hydraulic dump for material or fill delivery',
          'Can be left on site as temporary dumpster',
          'Garbage and demolition debris removal',
          'Smaller heavy equipment/material delivery with integrated ramps'
        ]
      },
      
      servicesTitle: 'What We Haul',
      servicesSubheading: 'Kenscho Transport offers 24/7 emergency services for your transportation needs.',
      services: [
        {
          title: 'Vehicles',
          desc: 'Cars, trucks, SUVs, and recreational vehicles. Kenscho Transport ensures safe loading with hydraulic dovetail and electric winch.',
          icon: 'car'
        },
        {
          title: 'Excavation Equipment',
          desc: 'Excavators, skid steers, tractors, and construction machinery. Secure transport with professional tie-downs.',
          icon: 'truck'
        },
        {
          title: 'Cargo & Materials',
          desc: 'Building materials, palletized goods, machinery, and oversized items up to 30 feet.',
          icon: 'package'
        },
        {
          title: 'Material Delivery',
          desc: 'Kenscho Transport can haul gravel, earth, or even demolition and garbage debris to or from your site.',
          icon: 'truck'
        }
      ],
      
      serviceAreasTitle: 'Service Areas',
      serviceAreasDesc: 'Kenscho Transport provides hot shot transport services throughout the greater Montreal area and surrounding regions.',
      areas: ['Lanaudière', 'Laurentians', 'Laval', 'Montreal', 'South Shore', 'Long distance available'],
      
      whyChoose: 'Why Choose Kenscho Transport',
      whyPoints: [
        'Professional, insured transport services',
        'Hydraulic dovetail for safe vehicle loading',
        'Electric winch for heavy equipment',
        'Tarp services for weather protection',
        'Flexible scheduling and competitive rates',
        'Experienced operator with attention to detail'
      ],
      
      getQuote: 'Get a Transport Quote',
      schedulePickup: 'Schedule a Pickup',
      galleryTitle: 'Our Fleet in Action',
      transportCta: 'Ready to get started?',
      transportCtaDesc: 'Call now to schedule a pickup or get a quote for your transport needs.',
      transportCtaButton: 'Get a Quote'
    },
    fr: {
      tagline: 'Transport Kenscho',
      title1: 'Services de',
      title2: 'transport Hot Shot',
      desc: 'Transport Kenscho offre des services de transport fiables pour cargo, véhicules et équipement lourd. Desservant Montréal et les environs avec des solutions de transport professionnelles.',
      
      trailerTitle: 'Notre équipement',
      trailerSpecs: {
        title: 'Remorque plateau 30 pi',
        capacity: 'Capacité de 13 000 lb (5 900 kg)',
        features: [
          'Queue hydraulique pour chargement facile',
          'Treuil électrique pour traction sécurisée',
          'Équipement d\'arrimage professionnel',
          'Services de bâche pour protection météo'
        ]
      },
      
      dumpTrailerSpecs: {
        title: 'Remorque à benne 5 tonnes',
        capacity: '8 000 lb (3 600 kg) / 17 verges cubes',
        features: [
          'Benne hydraulique pour livraison de matériaux ou remblai',
          'Peut être laissée sur place comme conteneur temporaire',
          'Enlèvement des déchets et débris de démolition',
          'Livraison de petits équipements lourds/matériaux avec rampes intégrées'
        ]
      },
      
      servicesTitle: 'Ce que nous transportons',
      servicesSubheading: 'Transport Kenscho offre des services d\'urgence 24/7 pour vos besoins de transport.',
      services: [
        {
          title: 'Véhicules',
          desc: 'Voitures, camions, VUS et véhicules récréatifs. Transport Kenscho assure un chargement sécuritaire avec queue hydraulique et treuil électrique.',
          icon: 'car'
        },
        {
          title: 'Équipement d\'excavation',
          desc: 'Excavateurs, chargeuses compactes, tracteurs et machinerie de construction. Transport sécurisé avec arrimage professionnel.',
          icon: 'truck'
        },
        {
          title: 'Cargo et matériaux',
          desc: 'Matériaux de construction, marchandises palettisées, machinerie et articles surdimensionnés jusqu\'à 30 pieds.',
          icon: 'package'
        },
        {
          title: 'Livraison de matériaux',
          desc: 'Transport Kenscho peut transporter du gravier, de la terre ou même des débris de démolition et ordures vers ou depuis votre chantier.',
          icon: 'truck'
        }
      ],
      
      serviceAreasTitle: 'Zones de service',
      serviceAreasDesc: 'Transport Kenscho offre des services de transport hot shot dans toute la grande région de Montréal et les environs.',
      areas: ['Lanaudière', 'Laurentides', 'Laval', 'Montréal', 'Rive-Sud', 'Longue distance disponible'],
      
      whyChoose: 'Pourquoi choisir Transport Kenscho',
      whyPoints: [
        'Services de transport professionnels et assurés',
        'Queue hydraulique pour chargement sécuritaire des véhicules',
        'Treuil électrique pour équipement lourd',
        'Services de bâche pour protection météo',
        'Horaire flexible et tarifs compétitifs',
        'Opérateur expérimenté avec attention aux détails'
      ],
      
      getQuote: 'Obtenir un devis de transport',
      schedulePickup: 'Planifier une cueillette',
      galleryTitle: 'Notre flotte en action',
      transportCta: 'Prêt à commencer?',
      transportCtaDesc: 'Appelez maintenant pour planifier une cueillette ou obtenir un devis pour vos besoins de transport.',
      transportCtaButton: 'Obtenir un devis'
    }
  };

  const t = content[language];

  const iconMap = {
    car: Car,
    truck: Truck,
    package: Package,
    shield: Shield
  };

  // SEO Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Transport Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Kenscho Transport",
      "telephone": "+1-514-291-3737",
      "email": "Transport@Kenscho.ca",
      "address": { "@type": "PostalAddress", "addressRegion": "QC", "addressCountry": "CA" },
      "areaServed": ["Lanaudière", "Laurentians", "Laval", "Montreal"]
    },
    "name": language === 'fr' ? "Services de Transport" : "Transport Services",
    "description": language === 'fr' 
      ? "Services de transport hot shot pour équipements et matériaux. Remorques à plateaux, transport de véhicules, livraison 24/7."
      : "Hot shot transport services for equipment and materials. Flatbed trailers, vehicle transport, 24/7 delivery."
  };

  return (
    <div className="grain-texture pt-20" data-testid="transport-page">
      <Helmet>
        <title>{language === 'fr' 
          ? 'Transport et Livraison | Hot Shot 24/7 | Lanaudière, Laurentides, Laval, Montréal | Kenscho'
          : 'Transport & Delivery | 24/7 Hot Shot | Lanaudière, Laurentians, Laval, Montreal | Kenscho'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Services de transport hot shot à Lanaudière, Laurentides, Laval et Montréal. Transport d\'équipement, véhicules, matériaux. Remorques à plateaux, livraison 24/7. RBQ 5829 4596 01.'
          : 'Hot shot transport services in Lanaudière, Laurentians, Laval, and Montreal. Equipment, vehicle, material transport. Flatbed trailers, 24/7 delivery. RBQ 5829 4596 01.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'transport, livraison, hot shot, remorque plateau, transport équipement, transport véhicule, Lanaudière, Laurentides, Laval, Montréal, 24/7, urgence'
          : 'transport, delivery, hot shot, flatbed trailer, equipment transport, vehicle transport, Lanaudière, Laurentians, Laval, Montreal, 24/7, emergency'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/transport')}`} />
        
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/transport" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/transport" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/transport" />
        
        <meta property="og:title" content={language === 'fr' ? 'Transport et Livraison | Kenscho Transport' : 'Transport & Delivery | Kenscho Transport'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Services de transport hot shot professionnels. Équipement, véhicules, matériaux. 24/7.'
          : 'Professional hot shot transport services. Equipment, vehicles, materials. 24/7.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/transport')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-primary/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8 animate-fade-in-up">
            <img 
              src={TRANSPORT_LOGO} 
              alt="Kenscho Transport" 
              className="h-20 md:h-24 w-auto"
            />
          </div>
          <h1 className="text-5xl md:text-6xl tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            {t.title1} <span className="text-primary">{t.title2}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t.desc}
          </p>
        </div>
      </section>

      {/* What We Haul - Full Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-muted-foreground mb-6 text-lg">
            {t.servicesSubheading}
          </p>
          
          {/* CTA Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 md:p-8 mb-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h3 className="text-2xl font-light mb-2">{t.transportCta}</h3>
                <p className="text-muted-foreground">{t.transportCtaDesc}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedTransportEmail className="font-medium" />
                  </div>
                </div>
                <Button onClick={handleQuoteClick} className="rounded-full btn-animate">
                    {t.transportCtaButton} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.services.map((service, index) => {
              const Icon = iconMap[service.icon] || Truck;
              return (
                <div key={index} className="bg-card border border-border/60 p-6 rounded-lg text-center">
                  <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-7 w-7 text-accent" />
                  </div>
                  <h3 className="text-lg font-medium mb-3">{service.title}</h3>
                  <p className="text-muted-foreground text-sm">{service.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trailer Specs */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
              <img 
                src="https://customer-assets.emergentagent.com/job_construction-pro-38/artifacts/2nc0g81b_IMG_6637.jpeg"
                alt="Kenscho Transport flat deck trailer"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-accent mb-4">
                {t.trailerTitle}
              </p>
              <h2 className="text-4xl font-light tracking-tight mb-4">
                {t.trailerSpecs.title}
              </h2>
              <p className="text-2xl text-primary font-medium mb-8">
                {t.trailerSpecs.capacity}
              </p>
              
              <ul className="space-y-4">
                {t.trailerSpecs.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <span className="text-lg">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button onClick={handleQuoteClick} size="lg" className="rounded-full mt-10 btn-animate" data-testid="transport-quote-btn">
                  {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Dump Trailer - Mirrored Layout */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mt-24">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl font-light tracking-tight mb-4">
                {t.dumpTrailerSpecs.title}
              </h2>
              <p className="text-2xl text-primary font-medium mb-8">
                {t.dumpTrailerSpecs.capacity}
              </p>
              
              <ul className="space-y-4">
                {t.dumpTrailerSpecs.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                    <span className="text-lg">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button onClick={handleQuoteClick} size="lg" className="rounded-full mt-10 btn-animate" data-testid="dump-trailer-quote-btn">
                  {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <div className="order-1 lg:order-2">
              {/* Dump Trailer Carousel */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <img 
                    src={DUMP_TRAILER_IMAGES[dumpTrailerIndex]}
                    alt="Kenscho Transport dump trailer"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Navigation Arrows */}
                <button 
                  onClick={prevDumpImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={nextDumpImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              
              {/* Thumbnails */}
              <div className="flex justify-center gap-2 mt-4">
                {DUMP_TRAILER_IMAGES.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setDumpTrailerIndex(index)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === dumpTrailerIndex 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Dump trailer ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <GallerySection language={language} />

      {/* Service Areas + Why Choose */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Service Areas */}
            <div className="bg-foreground text-white rounded-lg p-8 md:p-10">
              <h3 className="text-2xl font-light mb-6">{t.serviceAreasTitle}</h3>
              <p className="text-white/70 mb-8">
                {t.serviceAreasDesc}
              </p>
              <ul className="space-y-3 mb-8">
                {t.areas.map((area, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-accent rounded-full" />
                    {area}
                  </li>
                ))}
              </ul>
              <Button onClick={handleQuoteClick} variant="secondary" className="w-full rounded-full" data-testid="transport-cta-btn">
                  {t.schedulePickup} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Why Choose Us */}
            <div>
              <h3 className="text-2xl font-light mb-8">{t.whyChoose}</h3>
              <div className="space-y-4">
                {t.whyPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Business Section - Transport Kenscho */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <GoogleBusinessSection profile="transport" showMap={true} />
        </div>
      </section>
    </div>
  );
}
